export type LoanStage =
  | 'App Received'
  | 'Credit & Pricing'
  | 'Submitted to UW'
  | 'Conditions'
  | 'Clear to Close'

export const loanStages: LoanStage[] = [
  'App Received',
  'Credit & Pricing',
  'Submitted to UW',
  'Conditions',
  'Clear to Close',
]

export type Task = {
  id: string
  label: string
  due: string
  done: boolean
  owner: 'You' | 'Processor' | 'Borrower'
}

export type ConditionStatus = 'Outstanding' | 'AI Matched' | 'Accepted' | 'Rejected'

export type AiRun = {
  id: string
  timestamp: string
  outcome: string
}

export type Condition = {
  id: string
  name: string
  category: 'Income' | 'Assets' | 'Credit' | 'Property' | 'Compliance'
  status: ConditionStatus
  document?: string
  source?: 'Borrower portal' | 'Manual upload'
  confidence?: number
  reasoning?: string
  history: AiRun[]
}

export type Loan = {
  id: string
  number: string
  borrower: string
  state: string
  amount: number
  stage: LoanStage
  processor: string
  closingDate: string
  daysInStage: number
  stalled: boolean
  tasks: Task[]
  conditions: Condition[]
}

export const loans: Loan[] = [
  {
    id: 'ln-1191',
    number: 'LN-1191',
    borrower: 'Aaron Whitfield',
    state: 'FL',
    amount: 512000,
    stage: 'App Received',
    processor: 'Handy N.',
    closingDate: 'Aug 28',
    daysInStage: 1,
    stalled: false,
    tasks: [
      { id: 't1', label: 'Review submitted 1003', due: 'Today', done: false, owner: 'You' },
      { id: 't2', label: 'Order credit report', due: 'Today', done: false, owner: 'You' },
      { id: 't3', label: 'Welcome call', due: 'Tomorrow', done: true, owner: 'You' },
    ],
    conditions: [
      {
        id: 'c1',
        name: 'Most recent 30 days of paystubs',
        category: 'Income',
        status: 'AI Matched',
        document: 'Whitfield_Paystub_0715.pdf',
        source: 'Borrower portal',
        confidence: 0.94,
        reasoning: 'Document is an employer paystub dated within the last 30 days.',
        history: [
          { id: 'r1', timestamp: 'Today 9:12 AM', outcome: 'Matched to condition · 94%' },
        ],
      },
      {
        id: 'c2',
        name: 'Two months bank statements — all pages',
        category: 'Assets',
        status: 'AI Matched',
        document: 'Chase_Stmt_June.pdf',
        source: 'Borrower portal',
        confidence: 0.71,
        reasoning: 'Bank statement detected, but only one of two required months present.',
        history: [
          { id: 'r2', timestamp: 'Today 9:12 AM', outcome: 'Matched to condition · 71%' },
          { id: 'r3', timestamp: 'Jul 28 4:40 PM', outcome: 'No match found' },
        ],
      },
      {
        id: 'c3',
        name: 'Homeowners insurance declaration page',
        category: 'Property',
        status: 'Outstanding',
        history: [],
      },
      {
        id: 'c4',
        name: 'Signed borrower authorization',
        category: 'Compliance',
        status: 'Accepted',
        document: 'Auth_Signed.pdf',
        source: 'Manual upload',
        confidence: 0.98,
        reasoning: 'Signature block and disclosure language match the authorization template.',
        history: [
          { id: 'r4', timestamp: 'Jul 29 2:02 PM', outcome: 'Accepted by Milan R.' },
          { id: 'r5', timestamp: 'Jul 29 1:58 PM', outcome: 'Matched to condition · 98%' },
        ],
      },
    ],
  },
  {
    id: 'ln-1184',
    number: 'LN-1184',
    borrower: 'Jasmine Cole',
    state: 'GA',
    amount: 275000,
    stage: 'Credit & Pricing',
    processor: 'Handy N.',
    closingDate: 'Aug 21',
    daysInStage: 2,
    stalled: false,
    tasks: [
      { id: 't4', label: 'Send pricing options', due: 'Today 4:00 PM', done: false, owner: 'You' },
      { id: 't5', label: 'Collect W-2s', due: 'Aug 2', done: false, owner: 'Borrower' },
    ],
    conditions: [
      {
        id: 'c5',
        name: 'W-2s for the last two years',
        category: 'Income',
        status: 'Outstanding',
        history: [],
      },
      {
        id: 'c6',
        name: 'Photo ID',
        category: 'Compliance',
        status: 'AI Matched',
        document: 'Cole_DL_scan.jpg',
        source: 'Manual upload',
        confidence: 0.88,
        reasoning: 'State-issued driver license detected with legible expiration date.',
        history: [{ id: 'r6', timestamp: 'Today 8:04 AM', outcome: 'Matched to condition · 88%' }],
      },
    ],
  },
  {
    id: 'ln-1176',
    number: 'LN-1176',
    borrower: 'Grace Lindqvist',
    state: 'NY',
    amount: 689000,
    stage: 'Submitted to UW',
    processor: 'Renee P.',
    closingDate: 'Aug 14',
    daysInStage: 9,
    stalled: true,
    tasks: [
      { id: 't6', label: 'Chase underwriter for decision', due: 'Overdue', done: false, owner: 'You' },
      { id: 't7', label: 'Confirm appraisal ordered', due: 'Today', done: false, owner: 'Processor' },
    ],
    conditions: [
      {
        id: 'c7',
        name: 'Appraisal report',
        category: 'Property',
        status: 'Outstanding',
        history: [],
      },
      {
        id: 'c8',
        name: 'Letter of explanation — credit inquiries',
        category: 'Credit',
        status: 'Rejected',
        document: 'LOE_draft.docx',
        source: 'Manual upload',
        confidence: 0.42,
        reasoning: 'Letter references address history, not the credit inquiries requested.',
        history: [
          { id: 'r7', timestamp: 'Jul 27 11:20 AM', outcome: 'Rejected by Milan R. — wrong LOE' },
          { id: 'r8', timestamp: 'Jul 27 11:02 AM', outcome: 'Matched to condition · 42%' },
        ],
      },
    ],
  },
  {
    id: 'ln-1168',
    number: 'LN-1168',
    borrower: 'Devon Price',
    state: 'FL',
    amount: 415000,
    stage: 'Clear to Close',
    processor: 'Handy N.',
    closingDate: 'Aug 4',
    daysInStage: 1,
    stalled: false,
    tasks: [
      { id: 't8', label: 'Confirm closing time with title', due: 'Today', done: false, owner: 'You' },
    ],
    conditions: [
      {
        id: 'c9',
        name: 'Final verification of employment',
        category: 'Income',
        status: 'Accepted',
        document: 'VOE_final.pdf',
        source: 'Borrower portal',
        confidence: 0.96,
        reasoning: 'Employer-signed verification dated within the closing window.',
        history: [{ id: 'r9', timestamp: 'Jul 30 3:31 PM', outcome: 'Accepted by Milan R.' }],
      },
    ],
  },
  {
    id: 'ln-1155',
    number: 'LN-1155',
    borrower: 'Robert & Ana Silva',
    state: 'FL',
    amount: 329500,
    stage: 'Conditions',
    processor: 'Renee P.',
    closingDate: 'Aug 18',
    daysInStage: 11,
    stalled: true,
    tasks: [
      { id: 't9', label: 'Callback — close on app', due: 'Today 11:30 AM', done: false, owner: 'You' },
      { id: 't10', label: 'Upload gift letter', due: 'Overdue', done: false, owner: 'Borrower' },
    ],
    conditions: [
      {
        id: 'c10',
        name: 'Gift letter with donor bank statement',
        category: 'Assets',
        status: 'Outstanding',
        history: [],
      },
      {
        id: 'c11',
        name: 'Updated payoff statement',
        category: 'Credit',
        status: 'AI Matched',
        document: 'Payoff_0729.pdf',
        source: 'Borrower portal',
        confidence: 0.83,
        reasoning: 'Payoff statement detected; good-through date is within 10 days.',
        history: [{ id: 'r10', timestamp: 'Yesterday 5:15 PM', outcome: 'Matched to condition · 83%' }],
      },
    ],
  },
]

export const conditionStatusTone: Record<ConditionStatus, string> = {
  Outstanding: 'bg-gray-100 text-gray-600',
  'AI Matched': 'bg-brand-soft text-brand-dark',
  Accepted: 'bg-success-soft text-success',
  Rejected: 'bg-danger-soft text-danger',
}
